import {
  ArrowRightIcon,
  CheckIcon,
  CreditCardIcon,
  LocationMarkerIcon,
  RssIcon
} from "@heroicons/react/outline";
import { isEmpty, values } from "lodash";
import moment from "moment";
import { stringifyUrl } from "query-string";
import React, { useCallback, useMemo } from "react";
import { Link, useHistory } from "react-router-dom";
import AlertMessage from "../../../../components/AlertMessage";
import { USD_CURRENCY_LOCALE_STRING_CONFIG } from "../../../../components/CurrencySelector";
import NavLink from "../../../../components/NavLink";
import { useAuthentication } from "../../../../context/authentication";
import { useBillingPlans } from "../../../../context/billingPlans/BillingPlansProvider";
import { useMembers } from "../../../../context/members";
import { useToast } from "../../../../context/toast";
import { useWorkspaces } from "../../../../context/workspaces";
import classNames from "../../../../utils/classNames";
import { formatDateToLocale } from "../../../../utils/dates";
import { Plan, PlanType } from "../../../../utils/types";
import PlanCard from "../../../Plans/PlanCard";
import PlansUpgrade from "../../../Plans/PlansUpgrade";
import BillingPaymentHistory from "./BillingPaymentHistory";
import CardList from "./BillingPaymentMethod/CardList";

interface Props {}

const BillingDetails: React.FC<Props> = () => {
  const {
    currentWorkspace,
    currentWorkspaceIsDowngradePlan,
    currentWorkspaceIsDowngradePrePaidPlan,
    currentWorkspaceIsDowngradePrePaidHotspot,
    currentWorkspaceIsDowngradePrePaidSimulation
  } = useWorkspaces();
  const {
    currentPlan,
    cancelDowngrade,
    defaultPaymentMethod,
    loading,
    plans,
    newPlan,
    setNewPlan,
    subscribePlan,
    getPrePaidPlanByType,
    subscriptionPrePaid,
    pricePerItem,
    getPlanByType
  } = useBillingPlans();
  const history = useHistory();
  const { openChat } = useAuthentication();
  const { isCurrentUserAdmin, members } = useMembers();
  const { setModalWarningConfig } = useToast();

  const hotspotPrePaidButtonName = useMemo(() => {
    const hotspotAmout =
      (currentWorkspace?.active_plan?.allowed_hotspots || 0) -
      (currentPlan?.hotspots || 0);

    if (hotspotAmout === newPlan.hotspotAmount) return undefined;

    if (hotspotAmout < newPlan.hotspotAmount) return "Upgrade";

    return "Downgrade";
  }, [
    currentPlan?.hotspots,
    currentWorkspace?.active_plan?.allowed_hotspots,
    newPlan.hotspotAmount
  ]);

  const simulationPrePaidButtonName = useMemo(() => {
    const simulationAmout =
      (currentWorkspace?.active_plan?.allowed_simulations || 0) -
      (currentPlan?.simulations || 0);

    if (simulationAmout === newPlan.simulationAmount) return undefined;

    if (simulationAmout < newPlan.simulationAmount) return "Upgrade";

    return "Downgrade";
  }, [
    currentPlan?.simulations,
    currentWorkspace?.active_plan?.allowed_simulations,
    newPlan.simulationAmount
  ]);

  const isPaidPlan = useMemo(
    () =>
      currentPlan &&
      currentWorkspace &&
      currentPlan.type !== PlanType.community,
    [currentPlan, currentWorkspace]
  );

  const renderCurrentWorkspaceLockedMessage = useMemo(() => {
    if (
      currentWorkspace?.active_plan.status !== "past_due" &&
      currentWorkspace?.active_plan.requires_action === undefined
    ) {
      return;
    }

    let message = (
      <span className="flex flex-col">
        <span>
          The subscription payment failed:{" "}
          <b>
            {currentWorkspace?.active_plan.requires_action ||
              "Your bank rejected the payment."}{" "}
          </b>
        </span>
        <span>
          You can pay the invoice{" "}
          <a
            className="font-semibold text-hotspotty-300 cursor-pointer truncate"
            href={currentWorkspace?.active_plan.requires_action_link}
            target="_blank"
            rel="noreferrer"
          >
            by clicking here
          </a>
          .
        </span>
      </span>
    );

    if (currentWorkspace?.active_plan.requires_action === "authentication") {
      message = (
        <span className="flex flex-col">
          <span>
            You need to authorize the subscription payment{" "}
            {currentWorkspace?.active_plan.requires_action_link ? (
              <a
                className="font-semibold text-hotspotty-300 cursor-pointer truncate"
                href={currentWorkspace?.active_plan.requires_action_link}
                target="_blank"
                rel="noreferrer"
              >
                here
              </a>
            ) : (
              <span>
                Code 1430 -{" "}
                <b
                  onClick={() => openChat()}
                  className="font-semibold text-hotspotty-300 cursor-pointer"
                >
                  Please contact support team
                </b>
              </span>
            )}
            .
          </span>
        </span>
      );
    }

    return (
      <AlertMessage
        type="warning"
        title="Credit card Error"
        message={message}
      />
    );
  }, [
    currentWorkspace?.active_plan.requires_action,
    currentWorkspace?.active_plan.requires_action_link,
    currentWorkspace?.active_plan.status,
    openChat
  ]);

  const renderPlanData = useCallback(
    (plan: Plan) => (
      <div className="flex flex-col items-center justify-center w-6/12">
        <span className="font-bold uppercase dark:text-white">{plan.name}</span>
        <span className="flex items-center text-2xl">
          <small className="mr-1">$</small>
          <b className="text-3xl dark:text-white mr-0.5">
            {(plan?.price || 0) / 100}
          </b>
          <small>/month</small>
        </span>
        <div className="mt-2">
          {values(plan.features)
            .filter((item) => item)
            .map((item, index) => (
              <div
                key={item}
                className={classNames(
                  index === 0 ? "font-bold" : "font-medium",
                  "flex break-all items-start text-left text-xs text-gray-800 dark:text-white py-2"
                )}
              >
                {index > 0 && (
                  <CheckIcon className="w-4 flex-shrink-0 text-green-400 mr-2" />
                )}
                {item.replaceAll("*", "")}
              </div>
            ))}
        </div>
      </div>
    ),
    []
  );

  return (
    <div className="flex flex-col">
      <div className="flex items-center pb-2 mb-5 border-b border-gray-200 dark:border-gray-500">
        <p className="text-2xl font-semibold">Billing details</p>
      </div>

      {!isCurrentUserAdmin && currentWorkspace && !isEmpty(members) && (
        <span className="text-sm">
          Your permissions do not allow access to the billing details for this
          workspace.
        </span>
      )}

      {(isCurrentUserAdmin || !currentWorkspace || isEmpty(members)) && (
        <>
          {renderCurrentWorkspaceLockedMessage}

          {(currentWorkspaceIsDowngradePlan ||
            currentWorkspaceIsDowngradePrePaidPlan) && (
            <AlertMessage
              type="info"
              title="Downgrade notice"
              message={
                <div className="flex flex-col">
                  {currentWorkspaceIsDowngradePlan &&
                    currentWorkspace?.downgrade && (
                      <span className="mb-1">
                        You chose to downgrade your subscription to{" "}
                        <b>{currentWorkspace?.downgrade?.future.plan.name}</b>{" "}
                        at the end of billing cycle, on{" "}
                        {formatDateToLocale(
                          currentWorkspace?.downgrade?.date,
                          true
                        )}
                        .
                      </span>
                    )}

                  {currentWorkspaceIsDowngradePrePaidPlan &&
                    currentWorkspace?.downgrade && (
                      <div className="flex flex-col">
                        {currentWorkspaceIsDowngradePrePaidHotspot && (
                          <>
                            <span className="mt-1 mb-05 font-bold">
                              Pre paid hotspots
                            </span>
                            <div className="flex flex-col">
                              <span className="text-gray-500 line-through font-medium">
                                Monthly Pre paid hotspot x
                                {
                                  currentWorkspace.downgrade.current["pre-paid"]
                                    .hotspot.quantity
                                }
                                , totaling{" "}
                                {Number(
                                  pricePerItem(
                                    currentWorkspace.downgrade.current[
                                      "pre-paid"
                                    ].hotspot.quantity,
                                    PlanType.prePaidHotspot
                                  ) *
                                    currentWorkspace.downgrade.current[
                                      "pre-paid"
                                    ].hotspot.quantity
                                ).toLocaleString(
                                  "en-US",
                                  USD_CURRENCY_LOCALE_STRING_CONFIG
                                )}
                                /month
                              </span>
                              <span className="text-hotspotty-300 font-semibold">
                                Monthly Pre paid hotspot x
                                {
                                  currentWorkspace.downgrade.future["pre-paid"]
                                    .hotspot.quantity
                                }
                                , totaling{" "}
                                {Number(
                                  pricePerItem(
                                    currentWorkspace.downgrade.future[
                                      "pre-paid"
                                    ].hotspot.quantity,
                                    PlanType.prePaidHotspot
                                  ) *
                                    currentWorkspace.downgrade.future[
                                      "pre-paid"
                                    ].hotspot.quantity
                                ).toLocaleString(
                                  "en-US",
                                  USD_CURRENCY_LOCALE_STRING_CONFIG
                                )}
                                /month
                              </span>
                            </div>
                          </>
                        )}

                        {currentWorkspaceIsDowngradePrePaidSimulation && (
                          <>
                            <span className="mt-1 mb-05 font-bold">
                              Pre paid simulations
                            </span>
                            <div className="flex flex-col">
                              <span className="text-gray-500 line-through font-medium">
                                Monthly Pre paid simulation x
                                {
                                  currentWorkspace.downgrade.current["pre-paid"]
                                    .simulation.quantity
                                }
                                , totaling{" "}
                                {Number(
                                  pricePerItem(
                                    currentWorkspace.downgrade.current[
                                      "pre-paid"
                                    ].simulation.quantity,
                                    PlanType.prePaidSimulation
                                  ) *
                                    currentWorkspace.downgrade.current[
                                      "pre-paid"
                                    ].simulation.quantity
                                ).toLocaleString(
                                  "en-US",
                                  USD_CURRENCY_LOCALE_STRING_CONFIG
                                )}
                                /month
                              </span>
                              <span className="text-hotspotty-300 font-semibold">
                                Monthly Pre paid simulation x
                                {
                                  currentWorkspace.downgrade.future["pre-paid"]
                                    .simulation.quantity
                                }
                                , totaling{" "}
                                {Number(
                                  pricePerItem(
                                    currentWorkspace.downgrade.future[
                                      "pre-paid"
                                    ].simulation.quantity,
                                    PlanType.prePaidSimulation
                                  ) *
                                    currentWorkspace.downgrade.future[
                                      "pre-paid"
                                    ].simulation.quantity
                                ).toLocaleString(
                                  "en-US",
                                  USD_CURRENCY_LOCALE_STRING_CONFIG
                                )}
                                /month
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                  {isCurrentUserAdmin && (
                    <span
                      onClick={() => {
                        setModalWarningConfig({
                          open: true,
                          title: "Cancel downgrade",
                          confirmButtonText: "Yes",
                          cancelButtonText: "No",
                          confirmButtonClassname:
                            "bg-hotspotty-400 hover:bg-hotspotty-500",
                          message: (
                            <div className="flex flex-col">
                              <span>
                                Are you sure you want to cancel your plan
                                downgrade?
                              </span>
                              <span className="text-hotspotty-300 italic mt-1">
                                *Do not close this modal while loading!
                              </span>
                            </div>
                          ),
                          resolve: async () => {
                            await cancelDowngrade();
                          }
                        });
                      }}
                      className="ml-auto text-left mt-3 font-medium text-hotspotty-300 cursor-pointer hover:text-hotspotty-400"
                    >
                      Cancel Downgrade
                    </span>
                  )}
                </div>
              }
            />
          )}

          <div className="flex items-center justify-start sm:flex-row">
            <div className="flex flex-col h-full w-6/12 sm:w-auto sm:mr-10">
              <span className="text-sm font-medium dark:text-gray-200">
                Next monthly bill
              </span>

              {currentPlan ? (
                <span className="text-2xl font-bold dark:text-gray-200 mt-1">
                  {currentWorkspace?.active_plan?.next_billing_amount || "---"}
                </span>
              ) : (
                <div className="animate-pulse">
                  <span className="flex w-20 h-7 mt-2 rounded bg-gray-300 dark:bg-gray-600" />
                </div>
              )}
            </div>

            <div className="flex flex-col h-full items-end sm:items-start w-6/12 sm:w-auto">
              <span className="text-sm font-medium dark:text-gray-200">
                Next payment due
              </span>

              {currentPlan && currentWorkspace ? (
                <span className="text-2xl font-bold dark:text-gray-200 mt-1">
                  {currentWorkspace.active_plan?.next_billing_date &&
                  !currentWorkspace.active_plan?.on_grace
                    ? moment(
                        currentWorkspace.active_plan?.next_billing_date
                      ).format("MMMM Do")
                    : "---"}
                </span>
              ) : (
                <div className="animate-pulse">
                  <span className="flex w-20 h-7 mt-2 rounded bg-gray-300 dark:bg-gray-600" />
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center mt-10 pb-2 mb-3 border-b border-gray-200 dark:border-gray-500">
            <p className="text-2xl font-semibold">Payment method</p>
          </div>

          <div className="flex w-full flex-col">
            {!defaultPaymentMethod && !loading.paymentMethods ? (
              <span className="text-xs dark:font-light dark:text-white">
                You don’t have a payment method yet,{" "}
                <Link
                  to={"/workspace/settings/billing/payment-method/new"}
                  className="font-bold text-hotspotty-400 dark:text-hotspotty-300"
                >
                  click here
                </Link>{" "}
                to add a new one.
              </span>
            ) : (
              <CardList
                onClickCard={() =>
                  history.push("/workspace/settings/billing/payment-method")
                }
              />
            )}
          </div>

          <div className="flex justify-between items-center mt-10 pb-2 mb-2 border-b border-gray-200 dark:border-gray-500">
            <p className="text-2xl font-semibold">Subscription</p>
          </div>

          <div className="flex justify-start">
            <div
              className={classNames(
                "horizontal-snap-scroll py-3 flex w-full h-full justify-start items-center pr-2"
              )}
            >
              {!isEmpty(plans)
                ? values(plans).map((plan) => (
                    <PlanCard
                      isMobile={true}
                      plan={plan}
                      withHover={false}
                      showSelectedPlan={false}
                      showActionButton={!currentWorkspaceIsDowngradePlan}
                      showFeaturesLink={!currentWorkspaceIsDowngradePlan}
                      className="mx-1 lg:mx-2"
                      key={plan.uuid}
                      style={{ minWidth: "300px", maxWidth: "300px" }}
                      onClickToShowUpgrade={() => {
                        if (currentPlan?.type === PlanType.community) {
                          history.push("/pricing");

                          return;
                        }

                        if (currentPlan && currentPlan?.order > plan.order) {
                          setModalWarningConfig({
                            open: true,
                            title: "Downgrade plan",
                            withoutIcon: true,
                            cancelButtonText: "Cancel",
                            confirmButtonText: "Downgrade",
                            loadingButtonText: "Downgrading",
                            confirmButtonClassname:
                              "dark:bg-gray-700 dark:border-gray-700 border-gray-300 text-gray-700 dark:text-white",
                            cancelButtonClassname:
                              "dark:bg-hotspotty-400 dark:text-white dark:border-hotspotty-400 bg-gray-700 border-gray-700 text-white",
                            resolve: async () => {
                              await subscribePlan(
                                true,
                                plan,
                                "You have successfully downgraded your plan. Go to your workspace and check it out!"
                              );
                            },
                            message: (
                              <div className="flex flex-col w-full justify-center items-center">
                                <span className="text-sm ">
                                  You are downgrading from the{" "}
                                  <b className="dark:text-white">
                                    {currentPlan.name}
                                  </b>{" "}
                                  plan to the{" "}
                                  <b className="dark:text-white">{plan.name}</b>{" "}
                                  plan. By confirming, your plan will be
                                  scheduled to downgrade at the end of the
                                  current billing cycle{" "}
                                  <b>
                                    {formatDateToLocale(
                                      moment().add(1, "M").toString()
                                    )}
                                  </b>
                                  .
                                </span>

                                <div className="flex mt-8 w-full justify-center">
                                  {renderPlanData(currentPlan)}

                                  <ArrowRightIcon className="w-6 h-6 ml-2 font-bold dark:text-white mt-6" />

                                  {renderPlanData(plan)}
                                </div>

                                <div className="flex w-full flex-col mt-5">
                                  Prepaid hotspots and simulations should be
                                  downgraded separately.
                                </div>
                              </div>
                            )
                          });

                          return;
                        }

                        if (!currentPlan) return;

                        setModalWarningConfig({
                          open: true,
                          title: "Upgrade plan",
                          withoutIcon: true,
                          cancelButtonText: "Cancel",
                          confirmButtonText: "Upgrade",
                          loadingButtonText: "Upgrading",
                          confirmButtonClassname:
                            "dark:bg-hotspotty-400 dark:text-white dark:border-hotspotty-400 bg-gray-700 text-white border-gray-700",
                          cancelButtonClassname:
                            "dark:bg-gray-700 dark:text-white dark:border-gray-700",
                          resolve: async () => {
                            await subscribePlan(
                              true,
                              plan,
                              "You successfully upgraded your workspace."
                            );
                          },
                          message: (
                            <div className="flex flex-col w-full justify-center items-center">
                              <span>
                                You are upgrading from the{" "}
                                <b className="dark:text-white">
                                  {currentPlan.name}
                                </b>{" "}
                                plan to the{" "}
                                <b className="dark:text-white">{plan.name}</b>{" "}
                                plan. Do you want to continue?
                              </span>

                              <div className="flex mt-8 w-full justify-center">
                                {renderPlanData(currentPlan)}

                                <ArrowRightIcon className="w-6 h-6 ml-2 font-bold dark:text-white mt-6" />

                                {renderPlanData(plan)}
                              </div>
                            </div>
                          )
                        });
                      }}
                      upgradeText={
                        currentPlan && currentPlan?.order > plan.order
                          ? "Downgrade"
                          : "Upgrade"
                      }
                    />
                  ))
                : dummyArray.map((item) => (
                    <div
                      key={item}
                      style={{ minWidth: "300px", maxWidth: "300px" }}
                      className={classNames(
                        "horizontal-snap-scroll-child mx-0.5",
                        "flex flex-col justify-between relative w-4/12 h-full mx-2 rounded-lg shadow-md border first:ml-0 last:mr-0 bg-white dark:bg-gray-900 dark:border-gray-500"
                      )}
                    >
                      <div className="flex flex-col items-center py-10 rounded-tl-lg rounded-tr-lg dark:bg-gray-800">
                        <span className="flex w-4/12 h-4 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />
                        <span className="flex w-7/12 h-4 mt-3 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />
                      </div>
                      <div className="flex flex-col flex-1 justify-between w-full py-5 px-5 rounded-bl-lg rounded-br-lg border-t dark:border-gray-500 dark:bg-gray-900">
                        <span className="flex w-6/12 h-4 mt-2 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />
                        <span className="flex w-8/12 h-4 mt-3 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />
                        <span className="flex w-/12 h-4 mt-3 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />

                        <div className="flex flex-col items-center mt-40">
                          <span className="flex w-10/12 h-4 mt-3 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />
                          <span className="flex w-full h-4 mt-3 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />
                        </div>
                      </div>
                    </div>
                  ))}
            </div>
          </div>

          <div className="flex flex-col w-full border bg-white shadow-md dark:border-gray-500 dark:bg-gray-800 rounded-lg mt-5">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center px-3 sm:px-5 py-4">
              <div className="flex-1 flex items-center">
                <img
                  className="h-10 w-10 sm:h-12 sm:w-12 flex-shrink-0 rounded-full mr-2 sm:mr-4"
                  src={stringifyUrl({
                    url: `https://avatars.hotspotty.net/api/bottts/11F5oumsLQLSFEqo4TQiGEHbzRtMeuaTj3YNsAtCeX2YHV9vkCP.svg`,
                    query: { w: 40, h: 40 }
                  })}
                  alt="Hotspot avatar"
                />
                <div className="flex flex-col w-full">
                  <p className="text-sm sm:text-base font-medium">Hotspots</p>
                  <span className="flex items-center text-xs sm:text-sm dark:text-white">
                    {currentWorkspace?.counts?.hotspot !== undefined ? (
                      <b className="dark:text-white mr-1">
                        {currentWorkspace?.counts?.hotspot}
                      </b>
                    ) : (
                      <span className="flex w-5 h-4 rounded bg-gray-300 dark:bg-gray-600 mr-1" />
                    )}
                    of
                    {currentWorkspace?.active_plan?.allowed_hotspots !==
                    undefined ? (
                      <b className="dark:text-white mx-1">
                        {currentWorkspace?.active_plan?.allowed_hotspots}
                      </b>
                    ) : (
                      <span className="flex w-5 h-4 rounded bg-gray-300 dark:bg-gray-600 mx-1" />
                    )}
                    hotspot
                    {(currentWorkspace?.active_plan?.allowed_hotspots || 0) ===
                    1
                      ? ""
                      : "s"}{" "}
                    used
                  </span>
                </div>
              </div>

              {currentPlan?.type === PlanType.community && (
                <NavLink
                  className="flex-1 text-hotspotty-300 hover:text-hotspotty-400 font-semibold mt-3 lg:-mt-3 text-xs sm:text-sm lg:text-right"
                  to="/pricing"
                  onClick={() => {
                    setNewPlan((prevState) => ({
                      ...prevState,
                      plan: getPlanByType(PlanType.pro)
                    }));
                  }}
                >
                  Upgrade plan and add more Hotspots
                </NavLink>
              )}
            </div>

            {isPaidPlan && (
              <PlansUpgrade
                className="border-0 shadow-none rounded-t-none -mt-5 p-3 sm:p-5"
                title="Hotspots"
                min={0}
                max={20000}
                subtitle={
                  <>
                    <div>
                      <b>{newPlan.hotspotAmount.toLocaleString(undefined)}</b>{" "}
                      hotspots
                    </div>
                    <div>
                      <b>
                        {(newPlan.hotspotAmount * 2).toLocaleString(undefined)}
                      </b>{" "}
                      locations
                    </div>
                  </>
                }
                buttonName={hotspotPrePaidButtonName}
                value={newPlan.hotspotAmountPrice}
                amount={newPlan.hotspotAmount}
                priceTiers={
                  getPrePaidPlanByType(PlanType.prePaidHotspot)?.price_tiers
                }
                onClickButton={async () => {
                  await subscriptionPrePaid(PlanType.prePaidHotspot);
                }}
                buttonNameLoading="Upgrading"
                onChange={(value) =>
                  setNewPlan((prevState) => ({
                    ...prevState,
                    hotspotAmount: value
                  }))
                }
              />
            )}
          </div>

          <div className="flex flex-col w-full border bg-white shadow-md dark:border-gray-500 dark:bg-gray-800 rounded-lg mt-5">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center px-3 sm:px-5 py-4">
              <div className="flex-1 flex items-center">
                <RssIcon
                  className="h-10 w-10 sm:h-12 sm:w-12 rounded-full flex-shrink-0 mr-2 sm:mr-4 dark:text-gray-200"
                  aria-hidden="true"
                />

                <div className="flex flex-col w-full">
                  <p className="text-sm sm:text-base font-medium">
                    Super Simulations
                  </p>
                  <span className="flex items-center text-xs sm:text-sm dark:text-white">
                    {currentWorkspace?.active_plan
                      ?.simulations_used_on_period !== undefined ? (
                      <b className="dark:text-white mr-1">
                        {
                          currentWorkspace?.active_plan
                            ?.simulations_used_on_period
                        }
                      </b>
                    ) : (
                      <span className="flex w-5 h-4 rounded bg-gray-300 dark:bg-gray-600 mr-1" />
                    )}
                    of
                    {currentWorkspace?.active_plan?.allowed_simulations !==
                    undefined ? (
                      <b className="dark:text-white mx-1">
                        {currentWorkspace.active_plan.allowed_simulations}
                      </b>
                    ) : (
                      <span className="flex w-5 h-4 rounded bg-gray-300 dark:bg-gray-600 mx-1" />
                    )}
                    super simulation
                    {(currentWorkspace?.active_plan?.allowed_simulations ||
                      0) === 1
                      ? ""
                      : "s"}{" "}
                    used this month
                  </span>
                </div>
              </div>

              {currentPlan?.type === PlanType.community && (
                <NavLink
                  className="flex-1 text-hotspotty-300 hover:text-hotspotty-400 font-semibold mt-3 lg:-mt-3 text-xs sm:text-sm lg:text-right"
                  to="/pricing"
                  onClick={() => {
                    setNewPlan((prevState) => ({
                      ...prevState,
                      plan: getPlanByType(PlanType.pro)
                    }));
                  }}
                >
                  Upgrade plan and add more Super Simulations
                </NavLink>
              )}
            </div>

            {isPaidPlan && (
              <PlansUpgrade
                className="border-0 shadow-none rounded-t-none -mt-5 p-3 sm:p-5"
                title="Simulations per month"
                min={0}
                max={20000}
                subtitle={
                  <>
                    <div>
                      <b>
                        {newPlan.simulationAmount.toLocaleString(undefined)}
                      </b>{" "}
                      simulation{newPlan.simulationAmount === 1 ? "" : "s"}
                    </div>
                    <div>
                      <b>
                        {(newPlan.simulationAmount * 2).toLocaleString(
                          undefined
                        )}
                      </b>{" "}
                      locations
                    </div>
                  </>
                }
                buttonName={simulationPrePaidButtonName}
                value={newPlan.simulationAmountPrice}
                amount={newPlan.simulationAmount}
                priceTiers={
                  getPrePaidPlanByType(PlanType.prePaidSimulation)?.price_tiers
                }
                onClickButton={async () => {
                  await subscriptionPrePaid(PlanType.prePaidSimulation);
                }}
                onChange={(value) =>
                  setNewPlan((prevState) => ({
                    ...prevState,
                    simulationAmount: value
                  }))
                }
              />
            )}
          </div>

          <div className="flex flex-col w-full border bg-white shadow-md dark:border-gray-500 dark:bg-gray-800 rounded-lg mt-5">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center px-3 sm:px-5 py-4">
              <div className="flex-1 flex items-center">
                <LocationMarkerIcon
                  className="h-10 w-10 sm:h-12 sm:w-12 rounded-full flex-shrink-0 mr-2 sm:mr-4 dark:text-gray-200"
                  aria-hidden="true"
                />
                <div className="flex flex-col w-full">
                  <p className="text-sm sm:text-base font-medium">Locations</p>
                  <span className="flex items-center text-xs sm:text-sm dark:text-white">
                    {currentWorkspace?.counts?.location !== undefined ? (
                      <b className="dark:text-white mr-1">
                        {currentWorkspace?.counts?.location}
                      </b>
                    ) : (
                      <span className="flex w-5 h-4 rounded bg-gray-300 dark:bg-gray-600 mr-1" />
                    )}
                    of
                    {currentWorkspace?.active_plan?.allowed_locations !==
                    undefined ? (
                      <b className="dark:text-white mx-1">
                        {currentWorkspace?.active_plan?.allowed_locations}
                      </b>
                    ) : (
                      <span className="flex w-5 h-4 rounded bg-gray-300 dark:bg-gray-600 mx-1" />
                    )}
                    location
                    {(currentWorkspace?.active_plan?.allowed_locations || 0) ===
                    1
                      ? ""
                      : "s"}{" "}
                    used
                  </span>
                </div>
              </div>

              {currentPlan?.type === PlanType.community && (
                <NavLink
                  className="flex-1 text-hotspotty-300 hover:text-hotspotty-400 font-semibold mt-3 lg:-mt-3 text-xs sm:text-sm lg:text-right"
                  to="/pricing"
                  onClick={() => {
                    setNewPlan((prevState) => ({
                      ...prevState,
                      plan: getPlanByType(PlanType.pro)
                    }));
                  }}
                >
                  Upgrade plan and get more locations by adding Hotspots and/or
                  Super Simulations
                </NavLink>
              )}
            </div>
          </div>

          <BillingPaymentHistory />
        </>
      )}
    </div>
  );
};

export default BillingDetails;

//
// Utils
//

export const getCardIcon = (brand?: string) => {
  if (brand === "visa") {
    return <img src="/visa_logo.svg" className="w-6" alt="card" />;
  }

  if (brand === "mastercard") {
    return <img src="/master_logo.svg" className="w-6" alt="card" />;
  }

  return <CreditCardIcon className="w-5 h-5 text-black dark:text-white" />;
};

const dummyArray = ["1", "2", "3", "4"];
